import { EventBus }  from '../core/EventBus.js';
import { GameState } from '../core/GameState.js';
import { GAME_CONSTANTS } from '../config/constants.js';

/**
 * Passive analytics collector.
 * Listens to EventBus events, periodically snapshots GameState,
 * and sends anonymous telemetry via fire-and-forget POST.
 * Never writes to GameState — read-only observation.
 */
export class AnalyticsCollector {
  constructor() {
    this.sessionId = crypto.randomUUID();
    this.userId = this._getOrCreateUserId();
    this.sessionStart = Date.now();
    this.endpoint = GAME_CONSTANTS.ANALYTICS_ENDPOINT || '/api/analytics';
    this.snapshotInterval = (GAME_CONSTANTS.ANALYTICS_SNAPSHOT_INTERVAL_SECONDS || 300) * 1000;
    this._timer = null;
  }

  init() {
    // Session start
    this._send({
      type: 'session_start',
      screen: `${screen.width}x${screen.height}`,
      uaHash: this._hashUA(),
      timezone: this._getTimezone(),
      referrer: location.hostname !== document.referrer ? this._extractDomain(document.referrer) : '',
      hasSave: localStorage.length > 0 && !!Object.keys(localStorage).find(k => k.startsWith('seatrader_save_'))
    });

    // Periodic snapshots
    this._timer = setInterval(() => this._sendSnapshot(), this.snapshotInterval);

    // Game events
    EventBus.on('game:started', () => this._send({
      type: 'new_game',
      difficulty: GameState.difficulty,
    }));

    EventBus.on('game:loaded', () => this._send({
      type: 'game_loaded',
      difficulty: GameState.difficulty,
    }));

    EventBus.on('game:requestSave', () => this._sendSnapshot());

    EventBus.on('ship:purchased', ({ ship }) => this._send({
      type: 'ship_purchased',
      shipType: ship?.type?.id || ship?.typeId || '',
      fleetSize: GameState.ships.size,
    }));

    EventBus.on('ship:sold', ({ ship }) => this._send({
      type: 'ship_sold',
      shipType: ship?.type?.id || ship?.typeId || '',
      fleetSize: GameState.ships.size,
    }));

    EventBus.on('contract:completed', () => this._send({
      type: 'contract_completed',
    }));

    EventBus.on('contract:failed', () => this._send({
      type: 'contract_failed',
    }));

    EventBus.on('research:completed', ({ research }) => this._send({
      type: 'research_completed',
      researchId: research?.id || '',
    }));

    EventBus.on('player:milestone', ({ milestone }) => this._send({
      type: 'milestone_earned',
      milestoneId: milestone?.id || '',
      money: GameState.player?.money || 0,
      realPlayTime: this._realPlayTime(),
    }));

    // Session end
    const onEnd = () => this._send({
      type: 'session_end',
      duration: this._realPlayTime(),
    });
    window.addEventListener('beforeunload', onEnd);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') onEnd();
    });
  }

  // ---- snapshot ----

  _sendSnapshot() {
    if (!GameState.initialized || !GameState.player) return;

    const p = GameState.player;
    const ships = GameState.getAllShips();

    this._send({
      type: 'snapshot',
      realPlayTime: this._realPlayTime(),
      gameDate: GameState.currentDate?.toISOString?.() || '',
      difficulty: GameState.difficulty,
      gameSpeed: GameState.gameSpeed,
      money: p.money,
      reputation: p.reputation,
      shipCount: ships.length,
      shipTypes: ships.map(s => s.type?.id || s.typeId || ''),
      totalCargoTEU: ships.reduce((sum, s) => sum + (s.cargoCapacityUsed || 0), 0),
      activeContracts: GameState.contracts.active.length,
      completedContracts: GameState.contracts.completed.length,
      researchCompleted: [...GameState.research.completed],
      permitsCompleted: [...GameState.permits.completed],
      portsVisited: p.statistics.portsVisited.size,
      warehouseCount: GameState.warehouses.size,
      tradeRouteCount: GameState.tradeRoutes.size,
      staffCount: GameState.routeManagers.size,
      milestonesEarned: [...p.statistics.milestonesEarned],
      totalRevenue: p.statistics.totalRevenue,
      totalExpenses: p.statistics.totalExpenses,
      settings: { ...GameState.settings },
    });
  }

  // ---- transport ----

  _send(event) {
    if (!GameState.settings.sendAnalytics) return;

    const payload = {
      ...event,
      sid: this.sessionId,
      uid: this.userId,
      ts: Date.now(),
    };

    try {
      // Use sendBeacon for session_end (works during page unload), fetch for rest
      const body = JSON.stringify(payload);
      if (event.type === 'session_end' && navigator.sendBeacon) {
        navigator.sendBeacon(this.endpoint, body);
      } else {
        fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {}); // fire-and-forget
      }
    } catch {
      // silently ignore
    }
  }

  // ---- helpers ----

  _realPlayTime() {
    return Math.round((Date.now() - this.sessionStart) / 1000);
  }

  _hashUA() {
    // Simple hash of user agent for browser-type aggregation, not fingerprinting
    const ua = navigator.userAgent;
    let h = 0;
    for (let i = 0; i < ua.length; i++) {
      h = ((h << 5) - h + ua.charCodeAt(i)) | 0;
    }
    return (h >>> 0).toString(16);
  }

  _extractDomain(url) {
    try { return new URL(url).hostname; }
    catch { return ''; }
  }

  _getOrCreateUserId() {
    const key = 'seatrader_analytics_uid';
    try {
      let uid = localStorage.getItem(key);
      if (!uid) {
        uid = crypto.randomUUID();
        localStorage.setItem(key, uid);
      }
      return uid;
    } catch {
      return 'unknown';
    }
  }

  _getTimezone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
    catch { return ''; }
  }
}
